import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import useDelayedPopup from '../hooks/useDelayedPopup';
import { LeadMagnetForm } from './lead/LeadMagnetForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POPUP_STORAGE_KEY = 'aw_lead_popup_seen';
const POPUP_DISPLAY_DELAY_MS = 8000;
const MIN_PHONE_DIGITS = 10;
const EMPTY_FORM = { name: '', phone: '', loanType: '' };

const trackPopupShown = () => {
  if (window.gtag) {
    window.gtag('event', 'lead_popup_shown', { event_category: 'Lead Magnet' });
  }
};

export const LeadMagnetPopup = () => {
  const [open, setOpen] = useDelayedPopup(POPUP_STORAGE_KEY, POPUP_DISPLAY_DELAY_MS, trackPopupShown);
  const [values, setValues] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = values.phone.replace(/[^0-9]/g, '');
    if (!values.name.trim() || cleanPhone.length < MIN_PHONE_DIGITS || !values.loanType) {
      toast.error('Please fill your name, a valid 10-digit phone number and loan type');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        name: values.name.trim(),
        phone: cleanPhone,
        service: values.loanType,
        message: 'Lead Magnet: Free Loan Eligibility Check request',
      });
      if (window.gtag) {
        window.gtag('event', 'lead_magnet_submit', {
          event_category: 'Lead Magnet',
          event_label: values.loanType,
          value: 1,
        });
      }
      if (window.fbq) {
        window.fbq('track', 'Lead', { content_name: 'Free Loan Eligibility Check' });
      }
      toast.success('Thank you! Our loan expert will call you within 24 hours.');
      setOpen(false);
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="lead-magnet-popup">
        <DialogHeader>
          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center mb-2">
            <BadgeCheck className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-2xl">Free Loan Eligibility Check</DialogTitle>
          <DialogDescription>
            Find out how much loan you qualify for — free expert assessment, no obligation.
          </DialogDescription>
        </DialogHeader>

        <LeadMagnetForm
          values={values}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
};

export default LeadMagnetPopup;
